import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

export function initials(name: string | null | undefined): string {
  const clean = (name ?? "").trim()
  if (!clean) return "?"
  const parts = clean.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/** Stable hue (0–360) derived from a string, for colourful but deterministic avatars. */
export function hueFor(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return h % 360
}

/** Avatar with image fallback to coloured initials. */
export function UserAvatar({
  name,
  seed,
  src,
  size = "default",
  className,
}: {
  name: string | null | undefined
  /** Stable key for the colour (defaults to name). */
  seed?: string
  src?: string | null
  size?: "sm" | "default" | "lg"
  className?: string
}) {
  const hue = hueFor(seed ?? name ?? "")
  return (
    <Avatar size={size} className={className}>
      {src && <AvatarImage src={src} alt="" />}
      <AvatarFallback
        className={cn("font-medium text-[0.7rem] group-data-[size=lg]/avatar:text-sm")}
        style={{
          backgroundColor: `oklch(0.92 0.035 ${hue})`,
          color: `oklch(0.4 0.075 ${hue})`,
        }}
      >
        <span className="dark:hidden">{initials(name)}</span>
        <span
          className="hidden size-full items-center justify-center rounded-full dark:flex"
          style={{ backgroundColor: `oklch(0.32 0.045 ${hue})`, color: `oklch(0.88 0.05 ${hue})` }}
        >
          {initials(name)}
        </span>
      </AvatarFallback>
    </Avatar>
  )
}
